import { Injectable, OnDestroy } from '@angular/core';
import { AudioChunk, TranscriptEntry } from '../../../models/stt-chunked.model';
import { EMPTY, Subject, BehaviorSubject, TimeoutError, catchError, concatMap, finalize, retry, takeUntil, tap, throwError, timeout, timer, filter } from 'rxjs';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { environment } from '../../../../environments/environment';
import { API_ROUTES } from '../../../util/route.constants';

export const MAX_QUEUE = environment.sttService.audioChunks.audioFileMaxQueue;
export const MAX_RETRIES = environment.sttService.audioChunks.audioFileMaxRetries;
// Per-ATTEMPT ceiling. The queue is serial (concatMap), so without this a single
// hung request blocks every later chunk and pending$ never reaches 0.
// Worst case per chunk = (MAX_RETRIES + 1) * UPLOAD_TIMEOUT_MS + backoff.
// Move to environment.sttService.audioChunks if it needs to be tunable per env.
export const UPLOAD_TIMEOUT_MS = environment.sttService.audioChunks.uploadTimeoutMs;

// A chunk flushed by Stop / pause / device-loss carries isFinal so it can bypass the queue cap.
type QueuedChunk = AudioChunk & { isFinal?: boolean };

@Injectable({
  providedIn: 'root'
})
export class UploadPipelineService implements OnDestroy {
  readonly API = environment.cva_url + API_ROUTES.STT_AUDIO_GATEWAY;
  readonly transcript$ = new Subject<TranscriptEntry>();
  // Non-fatal — a queue-full drop shouldn't stop the recording session,
  // just tell the user a segment was lost. Separate channel from any
  // future hard-failure signal so consumers don't conflate the two.
  readonly dropped$ = new Subject<{ seq: number, sessionId: string, authMessage?: string }>();
  // Lets consumers (component) know when the last in-flight chunk has
  // actually finished — not just when stop() was called. Used to clear
  // the "Finishing up" status cue at the right moment.
  readonly pending$ = new BehaviorSubject<number>(0);

  private queue$ = new Subject<AudioChunk>();
  private destroy$ = new Subject<void>();
  private pending = 0;
  private cancel$ = new Subject<string>();

  constructor(private http: HttpClient) {
    this.initPipeline();
  }

  //Wired by ScribeService. false = chunk belongs to a discarded/replaced session → never hits the network.
  isSessionActive: (sessionId: string) => boolean = () => true;

  cancelSession(sessionId: string): void { this.cancel$.next(sessionId); }

  private release(seq: number): void {
    this.pending = Math.max(0, this.pending - 1);
    this.pending$.next(this.pending);
    console.log(`[UploadPipeline] chunk ${seq} settled (pending=${this.pending})`);
  }

  enqueue(chunk: QueuedChunk): void {
    if (this.pending >= MAX_QUEUE) {
      if (!chunk.isFinal) {
        console.warn(`[UploadPipeline] Queue full (${MAX_QUEUE}) — dropping chunk ${chunk.seq}`);
        this.dropped$.next({ seq: chunk.seq, sessionId: chunk.sessionId });
        return;
      }
      // The final chunk is the user's last words — losing it silently after Stop
      // means lost dictation. Let it exceed the cap by one rather than drop it.
      console.warn(`[UploadPipeline] Queue full (${MAX_QUEUE}) but chunk ${chunk.seq} is FINAL — accepting over the cap`);
    }
    this.pending++;
    this.pending$.next(this.pending);
    console.log(`[UploadPipeline] enqueued chunk ${chunk.seq} (pending=${this.pending}, final=${!!chunk.isFinal})`);
    this.queue$.next(chunk);
  }

  ngOnDestroy(): void {
    // reset pending so a re-created service starts clean
    this.pending = 0;
    this.pending$.next(0);
    this.destroy$.next();
    this.destroy$.complete();
    this.transcript$.complete();
    this.dropped$.complete();
    this.pending$.complete();
    this.cancel$.complete()
  }

  private initPipeline(): void {
    this.queue$
      .pipe(
        concatMap(chunk => this.upload(chunk)),
        // top-level catchError restarts pipeline on unexpected termination
        catchError((err, caught) => {
          console.error('[UploadPipeline] Unexpected pipeline error — restarting', err);
          return caught; // resubscribe to the same observable
        }),
        takeUntil(this.destroy$),
      )
      .subscribe(entry => {
        // Server may answer 200 with an empty/null body (e.g. nothing recognised).
        // Treat as "no text", not as an error — and don't let a null reach consumers.
        if (!entry) {
          console.warn('[UploadPipeline] Empty response body — nothing to emit');
          return;
        }
        this.transcript$.next(entry);
      });
  }

  /**
   * Retry only failures that can plausibly succeed on a second try.
   * 4xx (auth, validation, payload too large…) are deterministic — retrying
   * them just blocks the serial queue for ~7s per chunk and makes queue-full
   * drops more likely.
   */
  private isRetryable(err: unknown): boolean {
    if (err instanceof TimeoutError) return true;
    if (err instanceof HttpErrorResponse) {
      return err.status === 0 || err.status === 408 || err.status === 429 || err.status >= 500;
    }
    return false;
  }

  private upload(chunk: AudioChunk) {
    if (!this.isSessionActive(chunk.sessionId)) { // queued chunk of a discarded/replaced session
      console.log(`[UploadPipeline] chunk ${chunk.seq} skipped — session no longer active`);
      this.release(chunk.seq);
      return EMPTY;
    }
    const form = new FormData();
    form.append('audio', chunk.wav, `chunk-${chunk.seq}.wav`);
    form.append('seq', String(chunk.seq));
    form.append('startTime', String(chunk.startTime));
    form.append('endTime', String(chunk.endTime));
    form.append('sessionId', chunk.sessionId);

    return this.http.post<TranscriptEntry>(this.API, form).pipe(
      // Must sit BEFORE retry so each attempt gets its own timer (and a timed-out
      // request is actually cancelled via unsubscribe).
      timeout(UPLOAD_TIMEOUT_MS),
      retry({
        count: MAX_RETRIES,
        delay: (err, attempt) => {
          if (!this.isRetryable(err)) {
            console.warn(`[UploadPipeline] Chunk ${chunk.seq} failed with non-retryable error — giving up`, err);
            return throwError(() => err);
          }
          const waitMs = 1000 * Math.pow(2, attempt - 1);
          console.warn(`[UploadPipeline] Chunk ${chunk.seq} attempt ${attempt}/${MAX_RETRIES} failed — retrying in ${waitMs}ms`, err);
          return timer(waitMs);
        }
      }),
      takeUntil(this.cancel$.pipe(filter(id => id === chunk.sessionId))), // completes silently: no transcript, no dropped
      //  tap() replaces the redundant inner concatMap that was decrementing twice
      // entry can be null on an empty 200 — optional-chain so a successful upload
      // is not misreported as a dropped chunk.
      tap(entry => {
        console.debug(`[UploadPipeline] Chunk ${entry?.seq ?? chunk.seq} transcribed`);
      }),
      // finalize() is the single decrement point — runs on success AND error
      finalize(() => this.release(chunk.seq)),

      // per-chunk catchError returns EMPTY so the queue continues
      catchError(err => {
        if (this.isAuthFailure(err)) {
          console.warn(`[UploadPipeline] Chunk ${chunk.seq} stopped: session ended`, err);
          const authMessage = err instanceof HttpErrorResponse ? '' : (err?.errorMessage || '');
          this.dropped$.next({ seq: chunk.seq, sessionId: chunk.sessionId, authMessage });
          return EMPTY; // no dropped$, so no "chunk dropped" toast
        }
        console.warn(
          `[UploadPipeline] Chunk ${chunk.seq} dropped (retries exhausted or non-retryable)`,
          err,
        );

        this.dropped$.next({ seq: chunk.seq, sessionId: chunk.sessionId });
        return EMPTY;
      }),
    );
  }

  // Session ended: the interceptor has already logged the user out (401/423), or its queued
  // refresh branch threw 'Session ended'. This is not a lost audio chunk.
  private isAuthFailure(err: unknown): boolean {
    const e = err as { status?: number; message?: string } | null;
    return e?.status === 401 || e?.status === 423 || e?.message === 'Session ended';
  }
}
